import type { Expr, PathSegment } from "./ast.js";
import { pathToString } from "./ast.js";
import { parseExpression, ExpressionError } from "./parser.js";
import { PIPES } from "./format.js";
import { FUNCTION_NAMES } from "./evaluate.js";
import type { DataSource, FieldSchema, FieldType } from "../types/data.js";

export type ValueType = FieldType | "any" | "null" | "unknown";

export interface TypeIssue {
  code: "unknown-root" | "unknown-field" | "unknown-pipe" | "unknown-function" | "syntax" | "type-mismatch" | "not-a-list" | "not-an-object";
  message: string;
  /** Character offset when known. */
  position?: number;
}

export interface TypeResult {
  type: ValueType;
  schema?: FieldSchema;
  issues: TypeIssue[];
}

/**
 * A static scope describing what names are visible and their schemas.
 * Mirrors the runtime `Scope` chain.
 */
export class SchemaScope {
  constructor(
    private readonly vars: Record<string, FieldSchema>,
    private readonly parent?: SchemaScope
  ) {}

  lookup(name: string): FieldSchema | undefined {
    if (Object.prototype.hasOwnProperty.call(this.vars, name)) return this.vars[name];
    return this.parent?.lookup(name);
  }

  child(vars: Record<string, FieldSchema>): SchemaScope {
    return new SchemaScope(vars, this);
  }

  entries(): Array<{ name: string; schema: FieldSchema; depth: number }> {
    const out: Array<{ name: string; schema: FieldSchema; depth: number }> = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let s: SchemaScope | undefined = this;
    let depth = 0;
    while (s) {
      for (const [name, schema] of Object.entries(s.vars)) if (!out.some((e) => e.name === name)) out.push({ name, schema, depth });
      s = s.parent;
      depth++;
    }
    return out;
  }
}

export const GLOBAL_SCHEMAS: Record<string, FieldSchema> = {
  page: {
    type: "object",
    label: "Page",
    fields: {
      number: { type: "number", label: "Page number" },
      total: { type: "number", label: "Total pages" }
    }
  },
  now: { type: "datetime", label: "Generated at" },
  theme: {
    type: "object",
    label: "Theme",
    fields: {
      name: { type: "string", label: "Theme name" },
      logo: { type: "image", label: "Logo" },
      colors: { type: "object", label: "Colours", fields: {} },
      typography: { type: "object", label: "Typography", fields: {} },
      meta: { type: "object", label: "Brand details", fields: {} }
    }
  }
};

export function dataSourceSchemas(sources: DataSource[]): Record<string, FieldSchema> {
  const out: Record<string, FieldSchema> = {};
  for (const s of sources) out[s.id] = { type: "object", label: s.label, description: s.description, fields: s.schema };
  return out;
}

export function createSchemaScope(sources: DataSource[], extraGlobals: Record<string, FieldSchema> = {}): SchemaScope {
  return new SchemaScope({ ...GLOBAL_SCHEMAS, ...extraGlobals }).child(dataSourceSchemas(sources));
}

/** Resolves a path through a schema scope, returning the field schema when known. */
export function resolvePathSchema(segments: PathSegment[], scope: SchemaScope): { schema?: FieldSchema; issue?: TypeIssue } {
  const first = segments[0];
  if (!first || first.kind !== "prop") return { issue: { code: "unknown-root", message: "Expression must start with a name" } };
  const root = scope.lookup(first.name);
  if (!root) return { issue: { code: "unknown-root", message: `Unknown data source or variable "${first.name}"` } };
  let cur: FieldSchema = root;
  let projected = false;
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i]!;
    const soFar = pathToString(segments.slice(0, i));
    if (seg.kind === "index") {
      if (cur.type !== "array") return { issue: { code: "not-a-list", message: `"${soFar}" is not a list and cannot be indexed` } };
      cur = cur.itemSchema ?? { type: "any" as FieldType };
      continue;
    }
    if (cur.type === "array") {
      // Implicit projection over a list: holdings.value
      if (!cur.itemSchema) return { schema: { type: "array" } };
      if (cur.itemSchema.type !== "object" || !cur.itemSchema.fields) return { issue: { code: "not-an-object", message: `Items of "${soFar}" have no field "${seg.name}"` } };
      const f: FieldSchema | undefined = cur.itemSchema.fields[seg.name];
      if (!f) return { issue: { code: "unknown-field", message: `Items of "${soFar}" have no field "${seg.name}"` } };
      projected = true;
      cur = { type: "array", itemSchema: f, label: f.label };
      continue;
    }
    if ((cur.type as string) === "any") return { schema: { type: "any" as FieldType } };
    if (cur.type !== "object") return { issue: { code: "not-an-object", message: `"${soFar}" is a ${cur.type} and has no field "${seg.name}"` } };
    if (cur.fields && Object.keys(cur.fields).length === 0 && ["colors", "typography", "meta"].includes(segments[i - 1]!.kind === "prop" ? (segments[i - 1] as { name: string }).name : "")) {
      // Open theme objects: any key is allowed.
      return { schema: { type: "string" } };
    }
    const f: FieldSchema | undefined = cur.fields?.[seg.name];
    if (!f) return { issue: { code: "unknown-field", message: `"${soFar}" has no field "${seg.name}"` } };
    cur = f;
  }
  void projected;
  return { schema: cur };
}

function typeOfSchema(s: FieldSchema | undefined): ValueType {
  return (s?.type as ValueType) ?? "unknown";
}

/** Statically type-checks an expression against a schema scope. */
export function typeCheckAst(e: Expr, scope: SchemaScope): TypeResult {
  const issues: TypeIssue[] = [];
  const type = check(e, scope, issues);
  return { type: type.type, schema: type.schema, issues };
}

function check(e: Expr, scope: SchemaScope, issues: TypeIssue[]): { type: ValueType; schema?: FieldSchema } {
  switch (e.kind) {
    case "literal":
      return { type: e.value === null ? "null" : (typeof e.value as ValueType) };
    case "path": {
      const r = resolvePathSchema(e.segments, scope);
      if (r.issue) {
        issues.push(r.issue);
        return { type: "unknown" };
      }
      return { type: typeOfSchema(r.schema), schema: r.schema };
    }
    case "unary": {
      const t = check(e.arg, scope, issues);
      return { type: e.op === "not" ? "boolean" : t.type === "unknown" ? "unknown" : "number" };
    }
    case "binary": {
      const l = check(e.left, scope, issues);
      const r = check(e.right, scope, issues);
      switch (e.op) {
        case "and":
        case "or":
        case "==":
        case "!=":
        case "<":
        case "<=":
        case ">":
        case ">=":
        case "in":
          return { type: "boolean" };
        case "+":
          return { type: l.type === "string" || r.type === "string" ? "string" : "number" };
        default:
          return { type: "number" };
      }
    }
    case "conditional": {
      check(e.test, scope, issues);
      const a = check(e.then, scope, issues);
      check(e.else, scope, issues);
      return a;
    }
    case "call": {
      if (!FUNCTION_NAMES.includes(e.name)) issues.push({ code: "unknown-function", message: `Unknown function "${e.name}"` });
      for (const a of e.args) check(a, scope, issues);
      const numeric = ["sum", "count", "avg", "min", "max", "length", "round", "abs", "number", "year", "daysBetween"];
      if (numeric.includes(e.name)) return { type: "number" };
      if (["isEmpty", "isNotEmpty", "contains", "startsWith", "endsWith"].includes(e.name)) return { type: "boolean" };
      if (["concat", "string"].includes(e.name)) return { type: "string" };
      if (e.name === "now") return { type: "datetime" };
      if (["first", "last"].includes(e.name) && e.args[0]) {
        const t = check(e.args[0], scope, issues);
        return t.schema?.type === "array" ? { type: typeOfSchema(t.schema.itemSchema), schema: t.schema.itemSchema } : t;
      }
      return { type: "any" };
    }
    case "pipe": {
      const input = check(e.input, scope, issues);
      for (const a of e.args) check(a, scope, issues);
      const def = PIPES[e.name];
      if (!def) {
        issues.push({ code: "unknown-pipe", message: `Unknown formatter "${e.name}"` });
        return input;
      }
      if (def.returns === "same") return input;
      if (def.returns === "array") return { type: "array", schema: input.schema?.type === "array" ? input.schema : undefined };
      if (def.accepts && input.type !== "unknown" && input.type !== "any" && input.type !== "null" && !def.accepts.includes(input.type)) {
        const compat = (def.accepts.includes("number") && (input.type === "currency" || input.type === "percent")) || (def.accepts.includes("array") && input.type === "array");
        if (!compat) issues.push({ code: "type-mismatch", message: `Formatter "${e.name}" expects ${def.accepts.join(" or ")} but received ${input.type}` });
      }
      return { type: def.returns };
    }
    case "array":
      for (const i of e.items) check(i, scope, issues);
      return { type: "array" };
  }
}

/** Parses and type-checks an expression string. Syntax errors are returned as issues. */
export function typeCheck(src: string, scope: SchemaScope): TypeResult {
  try {
    return typeCheckAst(parseExpression(src), scope);
  } catch (err) {
    const pos = err instanceof ExpressionError ? err.position : undefined;
    return { type: "unknown", issues: [{ code: "syntax", message: (err as Error).message, position: pos }] };
  }
}
